import { useState } from 'react'

import FlagOutlinedIcon from '@ant-design/icons/FlagOutlined'

import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { RoleGuard } from '@shared/providers/src/MeProvider'
import { handleError, UserRole } from '@shared/utils'

import AssessmentAnswers from '@components/AssessmentAnswers'
import Section from '@components/Details/Section'
import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import PatientAccessGuard from '@components/PatientAccessGuard'

import { useAssessmentMarkExceptional } from '../AssessmentDetailsModal.hooks'

export default function AnswersSection({ assessment, patientId, canMarkExceptional = false }) {
  if (!assessment.answers || assessment.answers.length === 0) return null

  return (
    <Section
      paper
      mini
      title="Response"
      action={<Actions canMarkExceptional={canMarkExceptional} patientId={patientId} assessment={assessment} />}
    >
      <AssessmentAnswers data={assessment.answers} sx={{ p: 2 }} />
    </Section>
  )
}

AnswersSection.Loading = function () {
  return (
    <Section paper mini title="Response">
      <Skeleton variant="rectangular" height={300} />
    </Section>
  )
}

function Actions({ canMarkExceptional, patientId, assessment }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  const markExceptional = useAssessmentMarkExceptional(assessment.id)

  const handleClose = () => {
    setOpen(false)
    setTimeout(() => setReason(''), 300)
  }

  const handleConfirm = () => {
    return markExceptional
      .mutateAsync({ value: true, reason: reason.trim() })
      .catch((e) => handleError(e, { showResponse: true }))
      .finally(handleClose)
  }

  if (canMarkExceptional && !assessment.exceptional) {
    return (
      <RoleGuard allowed={[UserRole.Provider]}>
        <PatientAccessGuard patientId={patientId}>
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => setOpen(true)}
            endIcon={<FlagOutlinedIcon />}
            loading={markExceptional.isPending}
          >
            Flag
          </Button>
          <ConfirmationV2
            open={open}
            variant="warning"
            title="Mark as Flagged"
            description={
              <Stack spacing={2} sx={{ width: '100%', mt: 2 }}>
                <TextField
                  multiline
                  autoFocus
                  fullWidth
                  label="Reason"
                  placeholder="Please provide a reason for flagging this assessment"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  minRows={3}
                  maxRows={6}
                  helperText="This action will remove this assessment from the pending health assessments queue for all other users."
                />
              </Stack>
            }
            rejectLabel="Cancel"
            confirmLabel="Confirm"
            confirmDisabled={!reason.trim()}
            onReject={handleClose}
            onConfirm={handleConfirm}
            loading={markExceptional.isPending}
          />
        </PatientAccessGuard>
      </RoleGuard>
    )
  }
}
